<script setup>
  import { ref, computed, onMounted } from 'vue';

  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from '@/stores/UserStore';

  import BaseModal from './base/BaseModal.vue';
  import Icon from "@/components/icons/Icon.vue";

  const props = defineProps({
    spotName: {
      type: String,
      required: true
    },
    profiles: {
      type: Array,
      required: true
    }
  });

  const spotStore = useSpotStore();
  const userStore = useUserStore();

  const showModal = defineModel('showProfileModal', { default: true });

  const refreshProfilesTimeout = ref(null);

  const saveToProfileHeader = computed(() => `Save ${spotStore.spotName} to Profile.`);
  const saveToProfileInstructions = computed(() => `Choose the profile${props.profiles.length > 1 ? 's' : ''} you would like to save <strong>${spotStore.spotName}</strong> to.`);

  const settings = computed(() => ({
    name: spotStore.spotName,
    lat: spotStore.lat,
    lon: spotStore.lon
  }));

  const favoriteId = computed(() => {
    return (profileId) => {
      const favoritedSpot = spotStore.isSpotFavoritedInProfile(profileId);
      return favoritedSpot ? favoritedSpot[1] : null;
    }
  });

  async function handleContainerProfileSelection(event) {
    const profileId = event.target.value;

    if (event.target.checked) {
      const data = await userStore.addSpotToProfile(profileId, spotStore.spotId, settings.value);
      
      if (data.status_code === 0) {
        event.target.setAttribute('data-favorite-id', data.status_message);
      }

      refreshProfiles()
    } else {
      const favoriteId = event.target.dataset.favoriteId;
      await userStore.removeSpotFromProfile(profileId, favoriteId);

      event.target.removeAttribute('data-favorite-id');
      refreshProfiles();
    }

  }

  const refreshProfiles = (delay = 250) => {
    if (refreshProfilesTimeout.value) {
      clearTimeout(refreshProfilesTimeout.value);
    }

    refreshProfilesTimeout.value = setTimeout(() => {
      userStore.getProfiles(true);
      clearTimeout(refreshProfilesTimeout.value);

      refreshProfilesTimeout.value = null;
    }, delay);
  }

  onMounted(async () => {
    if (props.profiles.length === 1 && !favoriteId.value(props.profiles[0].profile_id)) {
      await userStore.addSpotToProfile(props.profiles[0].profile_id, spotStore.spotId, settings.value);
      refreshProfiles(0);
    }    
  })

</script>

<template>
  <BaseModal
    v-model="showModal"
    :allowOutsideClick="false"
  >
    <header class="jwx-modal__header">
      <h2 class="jwx-modal__title">{{ saveToProfileHeader }}</h2>
    </header>          
    <div class="jwx-modal__body">
      <div class="jwx-modal__section">
        <p v-html="saveToProfileInstructions" class="jwx-modal__text"></p>
      </div>
      <div class="jwx-modal__section">
        <ul class="jwx-save-to-selector">
          <li v-for="profile in profiles" class="jwx-save-to-selector__item" :key="profile.profile_id">
            <input
              @change="handleContainerProfileSelection"
              type="checkbox"
              :name="`jwx-${profile.profile_id}`"
              :id="`jwx-${profile.profile_id}`"
              :value="profile.profile_id"
              :checked="spotStore.isSpotFavoritedInProfile(profile.profile_id)"
              :data-favorite-id="favoriteId(profile.profile_id)"
              class="jwx-save-to-selector__input jwx-hidden-control"
            />
            <label :for="`jwx-${profile.profile_id}`" class="jwx-save-to-selector__label">
              <h4 class="jwx-save-to-selector__label-title">
                {{ profile.name }}
              </h4>
              <div class="jwx-save-to-selector__selection-indicator">
                <Icon name="checkmark" class="jwx-save-to-selector__indicator-icon" />
              </div>
            </label>
          </li>
        </ul>
      </div>
    </div>
    <div class="jwx-modal__footer">
      <div class="jwx-modal__actions">
        <button 
          type="button" 
          @click.prevent="showModal = false"
          class="jwx-button jwx-button--success"
        >
          Done
        </button>
      </div>
    </div>                  
  </BaseModal>  
</template>